export {};

type DemoPoint = [number, number];

type DemoCard = HTMLElement & {
  dataset: DOMStringMap & {
    base?: string;
    min?: string;
    max?: string;
    step?: string;
    unit?: string;
    precision?: string;
  };
};

type DemoState = {
  card: DemoCard;
  valueElement: HTMLElement;
  deltaElement: HTMLElement;
  sparklineElement: HTMLElement;
  rangeElement: HTMLElement;
  avgElement: HTMLElement;
  timeElement: HTMLElement;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  precision: number;
  history: DemoPoint[];
  lastUpdated: Date;
};

const cards = Array.from(document.querySelectorAll<DemoCard>("[data-demo-card]"));
const translationData = document.querySelector<HTMLElement>("[data-sensor-translations]")?.dataset || {};
const translations = {
  noData: translationData.noData || "no data",
  noDataSelectedRange: translationData.noDataSelectedRange || "No data in selected range",
  graphMissingGaps: translationData.graphMissingGaps || "Sensor graph with missing data gaps",
  justNow: translationData.justNow || "just now",
  secondsAgo: translationData.secondsAgo || "%(count)s sec ago",
  minutesAgo: translationData.minutesAgo || "%(count)s min ago",
};

function translate(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.split(`%(${key})s`).join(String(value)),
    template,
  );
}

const DEMO_WINDOW_MS = 24 * 60 * 60 * 1000;
const DEMO_GAP_THRESHOLD_MS = 4 * 60 * 60 * 1000;
const DEMO_MAX_POINTS = 48;

function numberFrom(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function integerFrom(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatNumber(value: number, precision: number): string {
  return value.toFixed(precision).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
}

function formatValue(value: number, precision: number, unit: string): string {
  const display = formatNumber(value, precision);
  return unit ? `${display} ${unit}` : display;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function nextValue(state: DemoState): number {
  if (state.min === state.max) return state.value;
  const direction = Math.random() > 0.5 ? 1 : -1;
  const movement = Math.random() * state.step * direction;
  return clamp(state.value + movement, state.min, state.max);
}

function generateHistory(value: number, min: number, max: number, step: number): DemoPoint[] {
  const now = Date.now();
  const points: DemoPoint[] = [];
  let current = value;

  for (let index = 0; index < DEMO_MAX_POINTS; index += 1) {
    const ratio = index / (DEMO_MAX_POINTS - 1);
    const timestamp = now - DEMO_WINDOW_MS + DEMO_WINDOW_MS * ratio;
    const wave = Math.sin(index / 4) * step * 1.8;
    const drift = (Math.random() - 0.5) * step * 2;
    current = clamp(current + wave * 0.15 + drift, min, max);
    points.push([timestamp, Number(current.toFixed(2))]);
  }

  return points;
}

function bucketPoints(points: DemoPoint[], maxPoints = DEMO_MAX_POINTS): DemoPoint[] {
  if (points.length <= maxPoints) return points;

  const bucketed: DemoPoint[] = [];
  const bucketSize = points.length / maxPoints;
  for (let index = 0; index < maxPoints; index += 1) {
    const start = Math.floor(index * bucketSize);
    const end = Math.max(start + 1, Math.floor((index + 1) * bucketSize));
    const slice = points.slice(start, end);
    const avgTime = slice.reduce((sum, point) => sum + point[0], 0) / slice.length;
    const avgValue = slice.reduce((sum, point) => sum + point[1], 0) / slice.length;
    bucketed.push([avgTime, Number(avgValue.toFixed(2))]);
  }
  return bucketed;
}

function clampSparklineCoord(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function renderSparklineLine(
  from: [number, number],
  to: [number, number],
  stroke: "#94a3b8" | "#f59e0b",
): string {
  return `<line x1="${from[0].toFixed(1)}" y1="${from[1].toFixed(1)}" x2="${to[0].toFixed(1)}" y2="${to[1].toFixed(1)}" stroke="${stroke}" stroke-width="1.4" stroke-linecap="round"/>`;
}

function renderSparkline(state: DemoState): void {
  const points = bucketPoints(state.history).sort((a, b) => a[0] - b[0]);
  if (points.length === 0) {
    state.sparklineElement.innerHTML = `<svg viewBox="0 0 100 28" preserveAspectRatio="none" class="h-full w-full" role="img" aria-label="${translations.noDataSelectedRange}">
      <line x1="2" y1="14" x2="98" y2="14" stroke="#f59e0b" stroke-width="1.4" stroke-linecap="round"/>
    </svg>`;
    return;
  }

  const nowMs = Date.now();
  const domainStart = nowMs - DEMO_WINDOW_MS;
  const domainEnd = nowMs;
  const domainRange = Math.max(domainEnd - domainStart, 1);
  const values = points.map((point) => point[1]);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  const w = 100;
  const h = 28;
  const pad = 1;

  const coords = points.map((point) => {
    const x = clampSparklineCoord(((point[0] - domainStart) / domainRange) * w);
    const y = pad + (h - 2 * pad) - ((point[1] - minVal) / range) * (h - 2 * pad);
    return [x, y] as [number, number];
  });

  let hasGap = false;
  const segments: string[] = [];
  const firstCoord = coords[0];
  const firstPoint = points[0];

  if (firstPoint[0] - domainStart > DEMO_GAP_THRESHOLD_MS && firstCoord[0] > 0) {
    hasGap = true;
    segments.push(renderSparklineLine([0, firstCoord[1]], firstCoord, "#f59e0b"));
  }

  coords.slice(0, -1).forEach((coord, index) => {
    const nextCoord = coords[index + 1];
    const isGap = points[index + 1][0] - points[index][0] > DEMO_GAP_THRESHOLD_MS;
    hasGap = hasGap || isGap;
    segments.push(renderSparklineLine(coord, nextCoord, isGap ? "#f59e0b" : "#94a3b8"));
  });

  const lastCoord = coords[coords.length - 1];
  const lastPoint = points[points.length - 1];
  if (domainEnd - lastPoint[0] > DEMO_GAP_THRESHOLD_MS && lastCoord[0] < w) {
    hasGap = true;
    segments.push(renderSparklineLine(lastCoord, [w, lastCoord[1]], "#f59e0b"));
  }

  state.sparklineElement.innerHTML = `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" class="h-full w-full"${hasGap ? ` role="img" aria-label="${translations.graphMissingGaps}"` : ' aria-hidden="true"'}>
    ${segments.join("")}
  </svg>`;
}

function renderDelta(state: DemoState): void {
  const reference = state.history[0]?.[1];
  if (reference === undefined) {
    state.deltaElement.textContent = `${translations.noData} /24h`;
    state.deltaElement.classList.remove("text-emerald-600", "text-rose-600");
    state.deltaElement.classList.add("text-slate-400");
    return;
  }

  const delta = state.value - reference;
  if (delta === 0) {
    state.deltaElement.innerHTML = `<span aria-hidden="true">&rarr;</span> 0${state.unit ? ` ${state.unit}` : ""} <span class="opacity-60 font-normal">/24h</span>`;
    state.deltaElement.classList.remove("text-emerald-600", "text-rose-600");
    state.deltaElement.classList.add("text-slate-400");
    return;
  }

  const isIncrease = delta > 0;
  const sign = isIncrease ? "+" : "-";
  state.deltaElement.innerHTML = `<span aria-hidden="true">${isIncrease ? "&uarr;" : "&darr;"}</span> ${sign}${formatValue(Math.abs(delta), state.precision, state.unit)} <span class="opacity-60 font-normal">/24h</span>`;
  state.deltaElement.classList.remove("text-slate-400", "text-emerald-600", "text-rose-600");
  state.deltaElement.classList.add(isIncrease ? "text-emerald-600" : "text-rose-600");
}

function renderRangeAndAverage(state: DemoState): void {
  if (state.history.length === 0) {
    state.rangeElement.innerHTML = `<span class="text-amber-600">${translations.noData}</span>`;
    state.avgElement.textContent = "";
    return;
  }

  const values = state.history.map((point) => point[1]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
  state.rangeElement.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline-block mr-1 opacity-70"><path d="m21 16-4 4-4-4"/><path d="M17 20V4"/><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/></svg><span>${formatNumber(min, state.precision)} – ${formatNumber(max, state.precision)}${state.unit ? " " + state.unit : ""}</span>`;
  state.avgElement.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline-block mr-1 opacity-70"><line x1="4" y1="12" x2="20" y2="12"/><circle cx="12" cy="6" r="2"/><circle cx="12" cy="18" r="2"/></svg><span>${formatNumber(avg, state.precision)}</span>`;
}

function renderState(state: DemoState): void {
  state.valueElement.textContent = formatValue(state.value, state.precision, state.unit);
  renderDelta(state);
  renderSparkline(state);
  renderRangeAndAverage(state);
}

function updateRelativeTimes(states: DemoState[]): void {
  const now = Date.now();
  states.forEach((state) => {
    const seconds = Math.max(0, Math.round((now - state.lastUpdated.getTime()) / 1000));
    if (seconds <= 2) {
      state.timeElement.textContent = translations.justNow;
      return;
    }
    if (seconds < 60) {
      state.timeElement.textContent = translate(translations.secondsAgo, { count: seconds });
      return;
    }
    state.timeElement.textContent = translate(translations.minutesAgo, {
      count: Math.floor(seconds / 60),
    });
  });
}

const states = cards.reduce<DemoState[]>((items, card) => {
  const valueElement = card.querySelector<HTMLElement>("[data-demo-value]");
  const deltaElement = card.querySelector<HTMLElement>("[data-demo-delta]");
  const sparklineElement = card.querySelector<HTMLElement>("[data-demo-sparkline]");
  const rangeElement = card.querySelector<HTMLElement>("[data-demo-range]");
  const avgElement = card.querySelector<HTMLElement>("[data-demo-avg]");
  const timeElement = card.querySelector<HTMLElement>("[data-demo-time]");
  if (!valueElement || !deltaElement || !sparklineElement || !rangeElement || !avgElement || !timeElement) {
    return items;
  }

  const value = numberFrom(card.dataset.base, 0);
  const precision = integerFrom(card.dataset.precision, 1);
  const unit = card.dataset.unit || "";
  const min = numberFrom(card.dataset.min, value - 1);
  const max = numberFrom(card.dataset.max, value + 1);
  const step = numberFrom(card.dataset.step, 0.2);
  const state: DemoState = {
    card,
    valueElement,
    deltaElement,
    sparklineElement,
    rangeElement,
    avgElement,
    timeElement,
    value,
    min,
    max,
    step,
    unit,
    precision,
    history: generateHistory(value, min, max, step),
    lastUpdated: new Date(),
  };
  state.history.push([Date.now(), value]);
  renderState(state);
  items.push(state);
  return items;
}, []);

function tick(): void {
  states.forEach((state) => {
    state.value = nextValue(state);
    state.lastUpdated = new Date();
    const point: DemoPoint = [Date.now(), state.value];
    state.history = [...state.history, point].filter(
      (point) => point[0] >= Date.now() - DEMO_WINDOW_MS,
    );
    renderState(state);
    state.card.classList.remove("sensor-card-updated");
    void state.card.offsetWidth;
    state.card.classList.add("sensor-card-updated");
    window.setTimeout(() => {
      state.card.classList.remove("sensor-card-updated");
    }, 1800);
  });
}

if (states.length) {
  window.setInterval(tick, 10000);
  window.setInterval(() => updateRelativeTimes(states), 1000);
}
